import { ProductFaq } from "../types";
import { settingsMarkers, soundMarkers, v1 } from "./protect-v1.shared";

export const protectV1NL: ProductFaq = {
	title: "Support en probleemoplossing",
	downloads: [
		{ group: "Nederlands", title: "Snel Start Gids", path: "/documents/downloads/2024-03-04_SnelStartGids_007561-R1B.pdf" },
		{ group: "Nederlands", title: "Referentiegids", path: "/documents/downloads/2024-06-07_ReferentieGids_NL_007562-R1C.pdf" },
		{ group: "Nederlands", title: "Veelgestelde vragen en Probleemoplossing", path: "/documents/downloads/2024-06-27_EarsOnly_Protect-FAQ+Troubleshooting-NL.pdf" },
		{ group: "English", title: "Quick Start Guide", path: "/documents/downloads/2024-02-19_QuickStart_007570_R1B.pdf" },
		{ group: "English", title: "Reference Guide", path: "/documents/downloads/2024-06-07_ReferenceGuide_EN_007562-R1C.pdf" },
		{ group: "English", title: "Frequently asked questions and Troubleshooting", path: "/documents/downloads/2024-06-10_EarsOnly_Protect-FAQ+Troubleshooting-EN.pdf" },
	],
	categories: [
		{
			id: "pairing",
			title: "Koppelen",
			questions: [
				{
					id: "pair",
					title: "Hoe koppel ik mijn EarsOnly Protect aan een apparaat?",
					blocks: [
						{ t: "p", text: "Download de MyEarsOnly app door de QR-code hieronder te scannen of via de [Google Play Store of Apple App Store](https://app.earsonly.nl/download). Volg de instructies in de app, of koppel EarsOnly Protect handmatig met een apparaat, zoals hieronder uitgelegd:" },
						{ t: "img", src: v1("scan_QRcode"), alt: "Scan de QR-code om de MyEarsOnly app te downloaden" },
						{ t: "ol", items: ["Zorg ervoor dat de Oplaadcase minimaal boven de 25% is opgeladen."] },
						{ t: "note", kind: "tip", text: "Ga naar {{q:battery}} voor hulp om op te laden en om het batterijniveau te controleren." },
						{
							t: "ol",
							start: 2,
							items: [
								"Zorg ervoor dat de Earbuds goed in de Oplaadcase zijn geplaatst.",
								"Houd de knop van de Oplaadcase voor 3 seconden ingedrukt. Wacht tot alle drie de lampjes knipperen (dit kan tot 10 seconden duren).",
								"Open het Bluetooth® menu van je apparaat en druk op “Verbind nieuw apparaat” (dit kan verschillen per apparaat, raadpleeg de bijbehorende handleiding).",
								"Als alle drie de lampjes van de Oplaadcase knipperen, zal “EarsOnly Protect” in deze lijst verschijnen.",
								"Druk op “EarsOnly Protect” om te verbinden.",
							],
						},
						{ t: "img", src: v1("pairing"), alt: "De Earbuds koppelen met je apparaat" },
					],
				},
			],
		},
		{
			id: "battery-category",
			title: "Batterij",
			questions: [
				{
					id: "battery",
					title: "Hoe controleer ik het batterijniveau en laad ik de batterijen op?",
					blocks: [
						{
							t: "ol",
							items: [
								"Open het deksel van de Oplaadcase.",
								"Controleer de batterijstatus van de Oplaadcase, welke kan worden afgelezen aan het middelste lampje (Afbeelding 1).",
								"Wanneer de batterij leeg is, kan de Oplaadcase worden aangesloten met behulp van de meegeleverde USB-C kabel en een USB-lader of USB-poort. De Oplaadcase kan ook draadloos worden opgeladen op een draadloze lader.",
							],
						},
						{ t: "img", src: v1("case_battery_light"), alt: "Het middelste lampje van de Oplaadcase toont de batterijstatus", caption: "Afbeelding 1" },
						{
							t: "ol",
							start: 4,
							items: [
								"Plaats de Earbud (links of rechts) op de overeenkomstige oplaadcontacten, zoals weergegeven in Afbeelding 2.",
								"Als de Earbud in de Oplaadcase wordt geplaatst, gaat het bijbehorende lampje voor links of rechts (Afbeelding 2) kort branden om aan te geven dat er goed contact is.",
								"Herhaal voor de andere Earbud.",
							],
						},
						{ t: "note", kind: "warning", text: "Zorg ervoor dat de oplaadcontacten van zowel de Earbuds als de Oplaadcase vrij zijn van vuil (zie: {{q:clean}}). Als er slecht of geen contact is, beginnen de Earbuds niet met laden." },
						{ t: "img", src: v1("right_earbud_in_case"), alt: "Een Earbud op de oplaadcontacten", caption: "Afbeelding 2" },
						{ t: "ol", start: 7, items: ["Sluit het deksel van de Oplaadcase."] },
						{ t: "note", kind: "note", text: "Optioneel: druk kort op de knop van de Oplaadcase (Afbeelding 3) om de huidige batterijstatus van de Earbuds en de Oplaadcase te bekijken. Na een paar seconden gaan de lampjes weer uit om energie te besparen." },
						{ t: "img", src: v1("pairing"), alt: "De knop op de Oplaadcase", caption: "Afbeelding 3" },
						{ t: "img", src: v1("earbuds_in_case"), alt: "De drie lampjes op de Oplaadcase", caption: "Afbeelding 4" },
						{
							t: "table",
							head: ["Lampje 1,2,3 (Afbeelding 4)", "Omschrijving"],
							rows: [
								["Aan", "Earbud/Oplaadcase is meer dan 25% opgeladen"],
								["Aan (Oranje)", "Earbud/Oplaadcase is minder dan 25% opgeladen"],
								["Aan (Rood)", "Earbud/Oplaadcase is minder dan 10% opgeladen"],
								["Uit", "Oplaadcase batterij is leeg, of er is geen verbinding tussen Earbud(s) en Oplaadcase oplaadcontacten (de lampjes gaan na een aantal seconden uit om batterij te besparen)."],
								["Fade animatie", "Oplaadcase is aan het opladen (wanneer het deksel dicht is, gaan de lampjes na een aantal seconden uit)."],
							],
						},
					],
				},
			],
		},
		{
			id: "maintenance",
			title: "Schoonmaken & onderhoud",
			questions: [
				{
					id: "clean",
					title: "Hoe maak ik mijn EarsOnly Protect schoon?",
					blocks: [
						{ t: "note", kind: "warning", text: "Gebruik geen agressieve schoonmaakmiddelen of oplosmiddelen, zoals benzeen, verdunner, afwasmiddel of alcohol, omdat deze het oppervlak kunnen beschadigen. Neem contact op met de fabrikant voor aanvullende informatie." },
						{ t: "h", text: "Earbuds" },
						{ t: "p", text: "Maak met een licht vochtigde doek de Earbuds aan de buitenkant schoon. Zorg ervoor dat ook de oplaadcontacten vrij van vuil zijn." },
						{ t: "img", src: v1("cleaning_earbuds"), alt: "De Earbuds schoonmaken" },
						{ t: "h", text: "Oplaadcase" },
						{ t: "p", text: "Maak met een licht vochtigde doek de binnen- en buitenkant van de Oplaadcase schoon. Zorg ervoor dat ook de oplaadcontacten vrij van vuil zijn." },
						{ t: "img", src: v1("cleaning_case"), alt: "De Oplaadcase schoonmaken" },
						{ t: "h", text: "Cerumenfilters" },
						{ t: "p", text: "Periodieke vervanging van de cerumenfilters (Wax guards) is vereist om een correcte werking te garanderen. Neem contact op met uw leidinggevende of wederverkoper als de oorsmeerfilters sterk vervuild of beschadigd zijn." },
						{ t: "img", src: v1("cleaning_filter"), alt: "Het cerumenfilter van een Earbud" },
						{ t: "note", kind: "note", text: "Zorg dat de Earbuds en Oplaadcase volledig droog zijn voordat u de Earbuds in de Oplaadcase plaatst." },
					],
				},
			],
		},
		{
			id: "app",
			title: "MyEarsOnly app",
			questions: [
				{
					id: "app-overview",
					title: "MyEarsOnly app",
					blocks: [
						{
							t: "annotated",
							image: v1("app-geluidsinstellingen"),
							alt: "Het geluidsscherm van de MyEarsOnly app",
							width: 303,
							height: 712,
							items: [
								{ icon: "A", title: "Connectie", text: "Laat zien of je Earbuds of Oplaadcase verbonden zijn met je apparaat.", ...soundMarkers.A },
								{ icon: "B", title: "Batterij status", text: "Laat de batterij status zien van de Earbuds en de Oplaadcase.", ...soundMarkers.B },
								{ icon: "Microphone", title: "Microfoon", text: "Laat zien welke microfoon wordt gebruikt bij een telefoongesprek." },
								{ icon: "Charging", title: "Opladen", text: "Laat zien of de Earbud of Oplaadcase aan het opladen is." },
								{ icon: "C", title: "Percentage", text: "Batterijpercentage van de Earbud of Oplaadcase.", ...soundMarkers.C },
								{ icon: "D", title: "Geluid/Instellingen", text: "Wissel tussen geluidsinstellingen en gebruikersinstellingen.", ...soundMarkers.D },
								{ icon: "E", title: "Gehoorbescherming", text: "Wissel tussen de verschillende gehoorbeschermingsmodi.", ...soundMarkers.E },
								{ icon: "F", title: "Audiobalans", text: "Pas aan en verfijn de audiobalans in elk oor.", ...soundMarkers.F },
								{ icon: "G", title: "Muziek equalizer", text: "Kies een audioprofiel dat past bij je favoriete muziekgenre.", ...soundMarkers.G },
							],
						},
					],
				},
				{
					id: "app-settings",
					title: "App instellingen",
					blocks: [
						{
							t: "annotated",
							image: v1("app-instellingen"),
							alt: "Het instellingenscherm van de MyEarsOnly app",
							width: 304,
							height: 712,
							items: [
								{ icon: "A", title: "Knopbediening", text: "Pas de functies aan die zijn toegewezen aan de tap- en drukknoppen van elke Earbud.", ...settingsMarkers.A },
								{ icon: "B", title: "Software-update", text: "Ophalen en installeren van updates.", ...settingsMarkers.B },
								{ icon: "C", title: "Hulp en veelgestelde vragen", ...settingsMarkers.C },
							],
						},
					],
				},
			],
		},
		{
			id: "troubleshooting",
			title: "Probleemoplossing",
			questions: [
				{
					id: "reset",
					title: "Reset Earbuds & Reset Oplaadcase",
					blocks: [
						{ t: "h", text: "Reset Earbuds" },
						{
							t: "ol",
							items: [
								"Haal de Earbuds uit de Oplaadcase.",
								"Houd de knoppen van de Earbuds voor 15 seconden ingedrukt.",
								"Plaats de Earbuds terug in de Oplaadcase en wacht 10 seconden.",
							],
						},
						{ t: "note", kind: "note", text: "Er worden geen (voorkeurs)instellingen gewijzigd." },
						{ t: "h", text: "Reset Oplaadcase" },
						{ t: "ol", items: ["Houd de knop van de Oplaadcase voor 15 seconden ingedrukt."] },
					],
				},
				{
					id: "not-charged",
					title: "Na lang opladen zijn mijn Earbuds niet opgeladen",
					blocks: [
						{
							t: "ol",
							items: [
								"Zorg ervoor dat de Earbuds goed in de Oplaadcase zijn geplaatst.",
								"Zorg ervoor dat de Oplaadcase is opgeladen (zie: {{q:battery}})",
								"Maak zowel de oplaadcontacten van de Earbuds als de Oplaadcase schoon met een licht vochtige doek. Plaats de Earbuds terug in de Oplaadcase en controleer of de lampjes oplichten.",
								"Mocht dit niet werken, voer dan een reset van de Earbuds en Oplaadcase uit en probeer opnieuw (zie: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "no-audio",
					title: "Mijn Earbuds spelen geen audio af",
					blocks: [
						{
							t: "ol",
							items: [
								"Zorg dat de Earbuds verbonden zijn met de audiobron.",
								"Zorg ervoor dat er audio wordt afgespeeld.",
								"Plaats de Earbuds in de Oplaadcase, wacht 10 seconden en pak ze weer uit de Oplaadcase.",
								"Nog steeds geen audio? Voer een reset van de Earbuds uit en probeer opnieuw (zie: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "wont-pair",
					title: "Mijn Earbuds kunnen niet gekoppeld worden met mijn apparaat",
					blocks: [
						{
							t: "ol",
							items: [
								"Zorg ervoor dat de Oplaadcase minimaal boven de 25% is opgeladen.",
								"Zorg ervoor dat de Earbuds goed in de Oplaadcase zijn geplaatst.",
								{
									text: "Controleer of beide Earbuds met de Oplaadcase zijn verbonden. Druk op de knop van de Oplaadcase. Controleer of alle drie de lampjes oplichten.",
									sub: [
										"Als alle lampjes oplichten, volg dan de koppelinstructies (zie: {{q:pair}}).",
										"Als er een lampje niet oplicht, heeft de bijbehorende Earbud geen verbinding met de Oplaadcase. Koppelen zal dan niet lukken: maak zowel de oplaadcontacten van de Earbuds als de Oplaadcase schoon met een licht vochtige doek. Plaats de Earbuds terug in de Oplaadcase en controleer of de lampjes oplichten. Als alle lampjes oplichten, volg dan de koppelinstructies.",
									],
								},
								"Mocht dit niet werken, voer dan een reset van de Earbuds en Oplaadcase uit en probeer opnieuw (zie: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "wont-connect",
					title: "Mijn Earbuds maken geen verbinding met mijn apparaat",
					blocks: [
						{
							t: "ol",
							items: [
								"Plaats de Earbuds in de Oplaadcase, wacht 10 seconden en haal ze er weer uit.",
								"Controleer of de Earbuds gekoppeld zijn aan je apparaat. Als dit niet het geval is volg dan de koppelinstructies (zie: {{q:pair}}).",
								"Mocht dit niet werken, voer dan een reset van de Earbuds uit en probeer opnieuw (zie: {{q:reset}}).",
								"Start het apparaat waarmee je probeert te verbinden opnieuw op en probeer opnieuw.",
								"Mocht dit ook niet werken, verwijder dan de Earbuds uit de Bluetooth-koppelingslijst op het apparaat en koppel de Earbuds opnieuw (zie: {{q:pair}}).",
							],
						},
					],
				},
				{
					id: "out-of-sync",
					title: "Audio loopt niet synchroon",
					blocks: [
						{
							t: "ol",
							items: [
								"Plaats de Earbuds in de Oplaadcase, wacht 10 seconden en haal ze er weer uit.",
								"Mocht dit niet werken, voer dan een reset van de Earbuds uit en probeer opnieuw (zie: {{q:reset}}).",
							],
						},
					],
				},
				{
					id: "factory-reset",
					title: "Terug naar de fabrieksinstellingen",
					blocks: [
						{ t: "note", kind: "warning", text: "Door terug te gaan naar fabrieksinstellingen verlies je de (voorkeurs)instellingen en de koppelingsinformatie met je apparaten." },
						{
							t: "ol",
							items: [
								"Zorg ervoor dat de Oplaadcase minimaal boven de 25% is opgeladen.",
								"Zorg ervoor dat de Earbuds goed in de Oplaadcase zijn geplaatst.",
								"Houd de knop van de Oplaadcase voor 3 seconden ingedrukt. Wacht tot alle drie de lampjes knipperen, dit kan tot 10 seconden duren.",
								"Druk twee keer snel achter elkaar op de knop van de Oplaadcase. De lampjes zouden nu in een andere kleur moeten knipperen.",
								"Zodra de lampjes weer in de witte kleur knipperen zijn de instellingen terug naar de fabrieksinstellingen. Je kan nu jouw EarsOnly Protect opnieuw koppelen aan een apparaat.",
							],
						},
					],
				},
			],
		},
	],
};
